import React, { useEffect, useState, useCallback } from "react";
import ListView from "./components/ListView";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import {
  getOverviewTableData,
  getNewOverviewTableData,
  exportOverviewTableData,
  getNotificationDataForOverview,
} from "../../redux/actions/overview";
import { OverviewCard } from "./components/OverviewCard";
import CalendarView from "./components/CalendarView";
import { Customer, Collaborator } from "../../constant/AuthenticationConstant";

const options = [
  "All",
  "completed",
  "pending",
  "overdue",
  "overdue in progress",
  "in progress",
  "completed with delay",
];
const overdueCondition = ["completed", "completed with delay", "extension"];

const getColor = (status, element) => {
  if (
    moment().isAfter(element.estimated_time, "date") &&
    !overdueCondition.includes(status)
  ) {
    return "#fecaca";
  } else {
    switch (status) {
      case "completed":
        return "#bbf7d0";
      case "completed with delay":
        return "#bbf7d0";
      case "pending":
        return "#fef08a";
      case "in progress":
        return "#fef08a";
      case "extension":
        return "#fed7aa";
      default:
        return "#e2e8f0";
    }
  }
};

const OverviewDashboard = (props) => {
  const { company_id, email } = props;
  const [allActivities, setAllActivities] = useState([]);
  const [activities, setActivities] = useState([]);
  const [events, setEvents] = useState([]);
  const [viewType, setViewType] = useState("list");
  const [sortField, setSortField] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [debouncedSearchText, setDebouncedSearchText] = useState("");

  const dispatch = useDispatch();
  const {
    overviewData,
    searchText,
    selected,
    lockToDoFilter,
    userTypeFilter,
    userFilter,
    todoFilter,
    groupedByFilter,
    dateRange,
  } = useSelector((state) => state.overview);
  const { row_count } = useSelector((state) => state.auth);

  const [updateTable, setUpdateTable] = useState(0);

  // Add loader in data table
  const [loader, setLoader] = useState(false);
  const [notificationLoader, setNotificationLoader] = useState(false);

  // Check if both company_id and email are present to determine embedded
  const isEmbedded = Boolean(company_id && email);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };
  const getOverviewData = () => {
    setLoader(true);
    const request = {
      onError: () => {
        setLoader(false);
      },
      onSuccess: () => {
        setLoader(false);
      },
    };
    if (viewType === "list") {
      dispatch(
        getNewOverviewTableData(
          // options[selected],
          selected[0] === 0 ? "" : selected.map((item) => options[item]),
          debouncedSearchText,
          currentPage,
          row_count,
          sortField,
          sortOrder,
          lockToDoFilter ? 0 : [1, 0].toString(),
          request,
          userTypeFilter,
          userFilter,
          todoFilter,
          groupedByFilter
        )
      );
    } else {
      dispatch(
        getNewOverviewTableData(
          selected[0] === 0 ? "" : selected.map((item) => options[item]),
          debouncedSearchText,
          "",
          "",
          "",
          "",
          lockToDoFilter ? 0 : [1, 0].toString(),
          request,
          userTypeFilter,
          userFilter,
          todoFilter,
          groupedByFilter,
          dateRange?.start_date,
          dateRange?.end_date
        )
      );
    }
  };

  useEffect(() => {
    getOverviewData();
  }, [
    updateTable,
    selected,
    debouncedSearchText,
    currentPage,
    sortField,
    sortOrder,
    lockToDoFilter,
    viewType,
    userTypeFilter,
    userFilter,
    todoFilter,
    groupedByFilter,
    dateRange,
    row_count
  ]);

  const handleViewType = (dashboardViewType) => {
    setViewType(dashboardViewType);
  };

  useEffect(() => {
    if (overviewData?.data?.length > 0) {
      if (viewType === "list") {
        const listActivity = [];
        overviewData.data.forEach((element) => {
          listActivity.push({
            job_id: element.job_id,
            activity_name: element.activity_name,
            activity_id: element.activity_id,
            is_extensible: Number(element.is_extensible),
            estimated_time: element.estimated_time,
            status: element.status,
            activity_assignment_id: element.activity_assignment_id,
            color: getColor(element.status, element),
            assignment_mapping_id: element.activity_assignment_mapping_id,
            is_transfered: element.is_transferred,
            is_locked: element.IsLocked,
            is_extend_requested: element.is_extend_requested,
            vendor_name: element.vendor_name,
            booking_controls: element.booking_controls,
            customer: element.customer,
          });
        });
        setAllActivities(listActivity);
        setActivities(
          listActivity.filter(
            (item) =>
              item.status === "pending" ||
              (moment().isAfter(item.endDate, "date") &&
                !overdueCondition.includes(item.status)) ||
              item.status === "extension"
          )
        );
      } else if (viewType === "calendar") {
        const calendarActivity = [];
        overviewData.data.forEach((element) => {
          if (element.estimated_time) {
            calendarActivity.push({
              id: element.activity_id,
              title: element.job_id,
              description: element.activity_name,
              date: moment(element.estimated_time).format("YYYY-MM-DD"),
              status: element.status,
              color: getColor(element.status, element),
              type: "activity",
              activity_assignment_id: element.activity_assignment_id,
              activity_id: element.activity_id,
              is_locked: element.IsLocked,
              is_extend_requested: element.is_extend_requested,
              booking_controls: element.booking_controls,
            });
          }
        });
        setAllActivities(calendarActivity);
      }
    } else {
      setAllActivities([]);
    }
  }, [overviewData.data, viewType]);

  useEffect(() => {
    setActivities(allActivities);
  }, [selected, allActivities]);

  const handleExport = () => {
    if (viewType === "list") {
      dispatch(
        exportOverviewTableData(
          // options[selected],
          selected[0] === 0 ? "" : selected.map((item) => options[item]),
          debouncedSearchText,
          currentPage,
          10,
          sortField,
          sortOrder,
          lockToDoFilter ? 0 : [1, 0].toString(),
          userTypeFilter,
          userFilter,
          todoFilter,
          groupedByFilter
        )
      );
    } else {
      dispatch(
        exportOverviewTableData(
          selected[0] === 0 ? "" : selected.map((item) => options[item]),
          debouncedSearchText,
          "",
          "",
          "",
          "",
          lockToDoFilter ? 0 : [1, 0].toString(),
          userTypeFilter,
          userFilter,
          todoFilter,
          groupedByFilter,
          dateRange?.start_date,
          dateRange?.end_date
        )
      );
    }
  };

  const handleNotificationData = () => {
    setNotificationLoader(true);
    const request = {
      onSuccess: () => {
        setNotificationLoader(false);
      },
      onError: () => {
        setNotificationLoader(false);
      },
    };
    dispatch(
      getNotificationDataForOverview(
        // options[selected],
        selected[0] === 0 ? "" : selected.map((item) => options[item]),
        debouncedSearchText,
        "",
        "",
        sortField,
        sortOrder,
        lockToDoFilter ? 0 : [1, 0].toString(),
        request,
        userTypeFilter,
        userFilter,
        todoFilter,
        groupedByFilter
      )
    );
  };

  return (
    <>
      <OverviewCard
        company_id={company_id}
        email={email}
        updateTable={updateTable}
        isEmbedded={isEmbedded}
        viewType={viewType}
        handleViewType={handleViewType}
        handleExport={handleExport}
        handleNotificationData={handleNotificationData}
        notificationLoader={notificationLoader}
      >
        {viewType === "calendar" ? (
          <CalendarView
            events={activities}
            setDebouncedSearchText={setDebouncedSearchText}
            loader={loader}
          />
        ) : (
          <ListView
            activities={activities}
            updateTable={updateTable}
            setUpdateTable={setUpdateTable}
            loader={loader}
            company_id={company_id}
            email={email}
            sortField={sortField}
            setSortField={setSortField}
            sortOrder={sortOrder}
            setSortOrder={setSortOrder}
            onPageChange={handlePageChange}
            setDebouncedSearchText={setDebouncedSearchText}
          />
        )}
      </OverviewCard>
    </>
  );
};

export default OverviewDashboard;
